import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  gradients,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
} from '../theme';

interface User {
  id: number;
  email: string;
  subscription_status: string;
  trial_end_date?: string;
}

interface SubscriptionDetails {
  subscription_status: string;
  trial_end_date?: string;
  subscription_details?: {
    status?: string;  // Raw Stripe status: "trialing", "active", "cancelled", etc.
    current_period_end?: string;
    cancel_at_period_end?: boolean;
  };
}

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const initPage = async () => {
      // Handle return from Stripe Checkout
      const isSuccess = searchParams.get('success') === 'true';
      const isCancelled = searchParams.get('cancelled') === 'true';

      if (isSuccess) {
        // Sync subscription status with Stripe before showing success
        try {
          const syncRes = await fetch(`${API_URL}/subscriptions/verify-and-sync`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (syncRes.ok) {
            const syncData = await syncRes.json();
            if (syncData.data?.synced) {
              setSuccess('Subscription successful! Your premium access is now active.');
            } else {
              setSuccess('Subscription successful! Your premium access is now active.');
            }
          }
        } catch (err) {
          console.error('Failed to sync subscription:', err);
          setSuccess('Subscription successful! Please refresh if status does not update.');
        }
      } else if (isCancelled) {
        setError('Checkout was cancelled. You can try again when ready.');
      }

      // Always sync with Stripe and fetch data
      // This ensures subscription status is correct even if webhooks failed
      await fetchData(true);

      // Clear the URL parameters after data is loaded
      if (isSuccess || isCancelled) {
        setSearchParams({});
      }
    };

    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchData = async (shouldSync = false) => {
    try {
      // Sync with Stripe to ensure database is up-to-date
      if (shouldSync) {
        try {
          await fetch(`${API_URL}/subscriptions/verify-and-sync`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (syncErr) {
          console.error('Sync failed:', syncErr);
        }
      }

      const userRes = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Only logout on 401 Unauthorized, not other errors
      if (userRes.status === 401) {
        localStorage.removeItem('auth_token');
        navigate('/login');
        return;
      }

      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
      }

      // Subscription status is optional - don't fail if it errors
      try {
        const subRes = await fetch(`${API_URL}/subscriptions/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (subRes.ok) {
          const subData = await subRes.json();
          setSubscription(subData.data);
        }
      } catch (subErr) {
        console.error('Failed to fetch subscription:', subErr);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      // Don't logout on general errors
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setActionLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/subscriptions/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to start checkout');
      }

      // Redirect to Stripe Checkout
      if (data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start subscription process');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    const isTrial = user?.subscription_status === 'trial';
    const message = isTrial
      ? 'Are you sure you want to cancel your trial? Your premium access will end immediately.'
      : 'Are you sure you want to cancel your subscription? Your premium access will end immediately.';

    if (!window.confirm(message)) {
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to cancel subscription');
      }

      const cancelMessage = isTrial
        ? 'Trial cancelled. You are now on the free plan.'
        : 'Subscription cancelled. You are now on the free plan.';
      setSuccess(cancelMessage);

      // Clear subscription state since it's been cancelled
      setSubscription(null);

      // Refresh data from server
      fetchData(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.starWhite,
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize.xl,
        letterSpacing: typography.letterSpacing.wider,
      }}>
        <span style={{ color: colors.accent.gold, marginRight: '12px' }}>★</span>
        Loading...
      </div>
    );
  }

  // Check subscription status from multiple sources to handle sync issues
  // Priority: subscription_details.status (raw Stripe) > subscription_status > user status
  const rawStripeStatus = subscription?.subscription_details?.status;
  const userStatus = subscription?.subscription_status || user?.subscription_status;

  // Determine if user has premium access (paid or trial)
  const isPremium = userStatus === 'premium' || rawStripeStatus === 'active';
  const isTrial = userStatus === 'trial' ||
    rawStripeStatus === 'trialing' ||
    (user?.trial_end_date && new Date(user.trial_end_date) > new Date());

  // User has premium access if they're either premium or on trial
  const hasPremiumAccess = isPremium || isTrial;

  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.2), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.5,
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{ maxWidth: '800px', margin: '0 auto 30px', position: 'relative', zIndex: 1 }}>
        <Link to="/dashboard" style={{
          color: colors.text.secondary,
          textDecoration: 'none',
          fontSize: typography.fontSize.sm,
        }}>
          ← Back to Dashboard
        </Link>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Title */}
        <div style={{ ...getCardStyle('default'), textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            fontSize: '56px',
            marginBottom: '16px',
            color: colors.accent.gold,
            textShadow: `0 0 30px rgba(212, 175, 55, 0.5)`,
          }}>★</div>
          <h1 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.starWhite,
            margin: '0 0 12px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>Subscription</h1>
          <p style={{ color: colors.text.secondary, margin: 0 }}>
            {isPremium ? 'Manage your Premium subscription' :
             isTrial ? 'Your trial is active' :
             'Upgrade to unlock all features'}
          </p>
        </div>

        {error && (
          <div style={{
            background: colors.status.errorBg,
            color: colors.status.error,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '24px',
            border: `1px solid rgba(248, 113, 113, 0.3)`,
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: colors.status.successBg,
            color: colors.status.success,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '24px',
            border: `1px solid rgba(74, 222, 128, 0.3)`,
          }}>
            {success}
          </div>
        )}

        {/* Current Status */}
        <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize.xl,
            color: colors.accent.gold,
            margin: '0 0 20px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>Current Plan</h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: isPremium ? colors.status.successBg :
                       isTrial ? colors.status.warningBg :
                       'rgba(74, 63, 138, 0.15)',
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.border.subtle}`,
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <div style={{
                fontSize: typography.fontSize['2xl'],
                fontWeight: typography.fontWeight.bold,
                color: isPremium ? colors.status.success :
                       isTrial ? colors.status.warning :
                       colors.text.secondary,
                textTransform: 'capitalize'
              }}>
                {isTrial ? 'Premium (Trial)' : isPremium ? 'Premium' : 'Free'} Plan
              </div>
              {isTrial && (
                <div style={{ color: colors.status.warning, fontSize: typography.fontSize.sm }}>
                  Trial ends: {user?.trial_end_date
                    ? new Date(user.trial_end_date).toLocaleDateString()
                    : subscription?.subscription_details?.current_period_end
                      ? new Date(subscription.subscription_details.current_period_end).toLocaleDateString()
                      : 'Soon'}
                  {' '}- then $3.99/month
                </div>
              )}
              {isPremium && !isTrial && subscription?.subscription_details?.current_period_end && (
                <div style={{ color: colors.status.success, fontSize: typography.fontSize.sm }}>
                  Next billing: {new Date(subscription.subscription_details.current_period_end).toLocaleDateString()}
                </div>
              )}
            </div>
            {isPremium && (
              <div style={{
                fontSize: typography.fontSize['2xl'],
                fontWeight: typography.fontWeight.bold,
                color: colors.status.success
              }}>
                $3.99/mo
              </div>
            )}
          </div>
        </div>

        {/* Pricing Plans - only show for free users (no premium access) */}
        {!hasPremiumAccess && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            {/* Free Plan */}
            <div style={{ ...getCardStyle('default') }}>
              <h3 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.xl,
                color: colors.text.starWhite,
                margin: '0 0 8px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>Free</h3>
              <div style={{
                fontSize: typography.fontSize['3xl'],
                fontWeight: typography.fontWeight.bold,
                color: colors.text.secondary,
                marginBottom: '24px'
              }}>
                $0<span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.normal }}>/month</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                {[
                  '3 calculations per month',
                  'Personal decisions only',
                  'Basic timing analysis',
                  'Email support'
                ].map((feature, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px',
                    color: colors.text.secondary,
                    fontSize: typography.fontSize.sm,
                  }}>
                    <span style={{ marginRight: '12px', color: colors.status.success }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <div style={{
                padding: '14px',
                background: 'rgba(74, 63, 138, 0.2)',
                borderRadius: borderRadius.md,
                textAlign: 'center',
                color: colors.text.muted,
              }}>
                Current Plan
              </div>
            </div>

            {/* Premium Plan */}
            <div style={{
              ...getCardStyle('gold'),
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '20px',
                background: gradients.goldButton,
                color: colors.background.midnight,
                padding: '6px 16px',
                borderRadius: borderRadius.full,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wider,
              }}>
                Best Value
              </div>
              <h3 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.xl,
                color: colors.accent.gold,
                margin: '0 0 8px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>Premium</h3>
              <div style={{
                fontSize: typography.fontSize['3xl'],
                fontWeight: typography.fontWeight.bold,
                color: colors.accent.gold,
                marginBottom: '24px'
              }}>
                $3.99<span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.normal, color: colors.text.secondary }}>/month</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                {[
                  'Unlimited calculations',
                  'All decision types',
                  'Crystal guidance',
                  'Advanced timing analysis',
                  'Priority support',
                  '7-day free trial'
                ].map((feature, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px',
                    color: colors.text.starWhite,
                    fontSize: typography.fontSize.sm,
                  }}>
                    <span style={{ marginRight: '12px', color: colors.accent.gold }}>★</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleSubscribe}
                disabled={actionLoading}
                style={{
                  ...getButtonStyle('gold'),
                  width: '100%',
                  opacity: actionLoading ? 0.7 : 1,
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {actionLoading ? 'Processing...' : 'Start Free Trial'}
              </button>
            </div>
          </div>
        )}

        {/* Subscription Management - show for users with premium access (premium or trial) */}
        {hasPremiumAccess && (
          <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
            <h2 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.xl,
              color: colors.accent.gold,
              margin: '0 0 24px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>{isTrial ? 'Trial Benefits' : 'Premium Benefits'}</h2>

            {/* Trial info banner */}
            {isTrial && user?.trial_end_date && (
              <div style={{
                padding: '16px',
                background: colors.status.warningBg,
                borderRadius: borderRadius.md,
                marginBottom: '24px',
                border: `1px solid rgba(251, 191, 36, 0.3)`,
              }}>
                <div style={{ color: colors.status.warning, fontWeight: typography.fontWeight.semibold, marginBottom: '4px' }}>
                  Trial Period Active
                </div>
                <div style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>
                  Your trial ends on {new Date(user.trial_end_date).toLocaleDateString()}.
                  You'll be charged $3.99/month after unless you cancel.
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '28px' }}>
              {[
                { icon: '\u221E', title: 'Unlimited Calculations', desc: 'No monthly limits' },
                { icon: '\u2605', title: 'All Decision Types', desc: 'Personal, Professional, Health' },
                { icon: '♦', title: 'Crystal Guidance', desc: 'Personalized recommendations' },
                { icon: '☆', title: 'Priority Support', desc: 'Fast response times' }
              ].map((benefit, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  background: 'rgba(74, 63, 138, 0.15)',
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.border.subtle}`,
                }}>
                  <div style={{
                    fontSize: '32px',
                    marginBottom: '12px',
                    color: colors.accent.gold,
                  }} dangerouslySetInnerHTML={{ __html: benefit.icon }} />
                  <div style={{
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.text.starWhite,
                    marginBottom: '6px',
                  }}>{benefit.title}</div>
                  <div style={{ fontSize: typography.fontSize.sm, color: colors.text.muted }}>{benefit.desc}</div>
                </div>
              ))}
            </div>

            {subscription?.subscription_details?.cancel_at_period_end ? (
              <div style={{
                padding: '16px',
                background: colors.status.warningBg,
                borderRadius: borderRadius.md,
                textAlign: 'center',
                color: colors.status.warning,
              }}>
                {isTrial
                  ? 'Your trial will end and you will not be charged.'
                  : 'Your subscription is set to cancel at the end of the billing period.'}
              </div>
            ) : (
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: colors.status.error,
                  border: `1px solid ${colors.status.error}`,
                  padding: '14px',
                  borderRadius: borderRadius.md,
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.7 : 1,
                  fontSize: typography.fontSize.base,
                  fontFamily: typography.fontFamily.body,
                  transition: 'all 0.3s ease',
                }}
              >
                {isTrial ? 'Cancel Trial' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        )}

        {/* FAQ */}
        <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize.xl,
            color: colors.accent.gold,
            margin: '0 0 24px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>Frequently Asked Questions</h2>
          {[
            {
              q: 'Can I cancel anytime?',
              a: 'Yes! You can cancel your subscription at any time. You\'ll continue to have access until the end of your billing period.'
            },
            {
              q: 'What happens after my trial?',
              a: 'After your 7-day free trial, you\'ll be charged $3.99/month. Cancel before the trial ends to avoid charges.'
            },
            {
              q: 'Is my payment information secure?',
              a: 'Absolutely. We use Stripe for payment processing, which is PCI-compliant and trusted by millions of businesses.'
            }
          ].map((faq, idx) => (
            <div key={idx} style={{
              padding: '16px 0',
              borderBottom: idx < 2 ? `1px solid ${colors.border.subtle}` : 'none'
            }}>
              <div style={{
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.starWhite,
                marginBottom: '8px'
              }}>{faq.q}</div>
              <div style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>{faq.a}</div>
            </div>
          ))}
        </div>

        {/* Back Link */}
        <div style={{ textAlign: 'center' }}>
          <Link to="/dashboard" style={{
            color: colors.accent.gold,
            textDecoration: 'none',
            fontSize: typography.fontSize.sm,
          }}>
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
